import { axiosInstance } from '@/lib/axios';

/**
 * Admin Service
 * Handles all admin-related API calls for user management, analytics, and municipality admin
 */

// Admin Types
export interface UserListRequest {
  limit?: number;
  offset?: number;
  role?: 'user' | 'admin';
  is_active?: boolean;
  search?: string;
}

export interface UserResponse {
  id: number;
  email: string;
  is_active: boolean;
  is_admin: boolean;
  account_balance: string;
  preferred_search_radius: number;
  email_notifications: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserListResponse {
  users: UserResponse[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface UserDetailResponse {
  user: UserResponse;
  transaction_count: number;
  total_credits: string;
  total_debits: string;
  last_activity: string | null;
}

export interface UserAdminUpdate {
  is_active?: boolean;
  role?: 'user' | 'admin';
}

export interface UsageAnalyticsResponse {
  total_users: number;
  active_users_30d: number;
  total_searches: number;
  searches_last_7d: number;
  searches_last_30d: number;
  avg_searches_per_user: number;
  new_users_30d: number;
}

export interface RevenuePeriodData {
  period: string;
  revenue: number;
  transaction_count: number;
}

export interface RevenueAnalyticsResponse {
  total_revenue: number;
  revenue_by_period: RevenuePeriodData[];
  average_transaction: number;
  topup_revenue: number;
  search_revenue: number;
  period: string;
}

export interface SuccessRateResponse {
  total_extractions: number;
  successful: number;
  failed: number;
  success_rate: number;
  failure_reasons: Record<string, number>;
  avg_confidence_score: number;
  avg_extraction_time_seconds: number;
  extractions_by_state: Record<string, number>;
}

export interface MunicipalityStatusResponse {
  city: string;
  municipality: string;
  state: string;
  status: string;
  last_verified: string | null;
  confidence: number;
  issues: string[];
}

export interface VerificationSummary {
  total: number;
  status_counts: Record<string, number>;
  avg_response_time: number;
  issues_found: number;
  timestamp: string;
}

export interface VerificationResponse {
  success: boolean;
  message: string;
  results_count: number;
  summary?: VerificationSummary;
}

export interface VerificationHistoryEntry {
  municipality: string;
  city: string;
  status: string;
  timestamp: string;
  response_time_ms: number;
  status_code: number | null;
  error_message: string | null;
  detected_changes: string[];
}

export interface VerificationHistoryResponse {
  total: number;
  results: VerificationHistoryEntry[];
}

export interface MunicipalityStatistics {
  total_municipalities: number;
  coverage_counties: string[];
  average_confidence: number;
  stale_municipalities: number;
  data_health_score: number;
  needs_attention: boolean;
  recommended_actions: string[];
}

export interface MessageResponse {
  message: string;
}

// Search Data Types

export interface AdminSearchRequest {
  limit?: number;
  offset?: number;
  start_date?: string;
  end_date?: string;
  state?: string;
  city?: string;
  extraction_status?: string;
}

export interface AdminSearchSummaryResponse {
  id: string;
  city: string;
  state: string;
  job_description: string;
  municipality_name: string | null;
  permit_type_name: string | null;
  total_estimated_fee: number | null;
  fee_confidence: string | null;
  extraction_status: string;
  created_at: string;
}

export interface AdminSearchListResponse {
  searches: AdminSearchSummaryResponse[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface AdminSearchDetailResponse {
  id: string;
  city: string;
  state: string;
  job_description: string;
  municipality_name: string | null;
  permit_type_name: string | null;
  total_estimated_fee: number | null;
  fee_confidence: string | null;
  extraction_confidence: number | null;
  extraction_status: string;
  fee_breakdown: Record<string, unknown> | null;
  extracted_fees: Record<string, unknown> | null;
  extracted_permit_info: Record<string, unknown> | null;
  search_cost: number;
  llm_cost: number | null;
  web_search_cost: number | null;
  llm_enhanced: boolean;
  error_message: string | null;
  processing_duration_ms: number | null;
  permit_link: string | null;
  user_rating: number | null;
  user_feedback: string | null;
  created_at: string;
}

// User Management API Calls

/**
 * Get list of all users with pagination and filtering
 */
export const getUsers = async (params?: UserListRequest): Promise<UserListResponse> => {
  const response = await axiosInstance.get<UserListResponse>('/admin/users', { params });
  return response.data;
};

/**
 * Get detailed information about a specific user
 */
export const getUserDetails = async (userId: number): Promise<UserDetailResponse> => {
  const response = await axiosInstance.get<UserDetailResponse>(`/admin/users/${userId}`);
  return response.data;
};

/**
 * Update user account settings (activate/deactivate, change role)
 */
export const updateUser = async (
  userId: number,
  updateData: UserAdminUpdate
): Promise<UserResponse> => {
  const response = await axiosInstance.put<UserResponse>(`/admin/users/${userId}`, updateData);
  return response.data;
};

/**
 * Delete (deactivate) a user account
 */
export const deleteUser = async (userId: number): Promise<MessageResponse> => {
  const response = await axiosInstance.delete<MessageResponse>(`/admin/users/${userId}`);
  return response.data;
};

// Analytics API Calls

/**
 * Get system usage analytics
 */
export const getUsageAnalytics = async (): Promise<UsageAnalyticsResponse> => {
  const response = await axiosInstance.get<UsageAnalyticsResponse>('/admin/analytics/usage');
  return response.data;
};

/**
 * Get revenue and financial analytics
 */
export const getRevenueAnalytics = async (
  startDate?: string,
  endDate?: string,
  period: 'day' | 'week' | 'month' = 'month'
): Promise<RevenueAnalyticsResponse> => {
  const response = await axiosInstance.get<RevenueAnalyticsResponse>(
    '/admin/analytics/revenue',
    {
      params: {
        start_date: startDate,
        end_date: endDate,
        period,
      },
    }
  );
  return response.data;
};

/**
 * Get permit extraction success rate and quality metrics
 */
export const getSuccessRateAnalytics = async (
  state?: string,
  days: number = 30
): Promise<SuccessRateResponse> => {
  const response = await axiosInstance.get<SuccessRateResponse>(
    '/admin/analytics/success-rates',
    {
      params: { state, days },
    }
  );
  return response.data;
};

// Municipality Management API Calls

/**
 * Get status of all municipalities in database
 */
export const getMunicipalitiesStatus = async (
  staleOnly: boolean = false
): Promise<MunicipalityStatusResponse[]> => {
  const response = await axiosInstance.get<MunicipalityStatusResponse[]>(
    '/admin/municipalities/status',
    {
      params: { stale_only: staleOnly },
    }
  );
  return response.data;
};

/**
 * Initiate verification of municipality data
 */
export const verifyMunicipalities = async (
  cities?: string[],
  staleOnly: boolean = false
): Promise<VerificationResponse> => {
  const response = await axiosInstance.post<VerificationResponse>(
    '/admin/municipalities/verify',
    null,
    {
      params: {
        cities: cities?.join(','),
        stale_only: staleOnly,
      },
    }
  );
  return response.data;
};

/**
 * Refresh all stale municipality data
 */
export const refreshStaleMunicipalities = async (
  maxAgeDays: number = 90
): Promise<VerificationResponse> => {
  const response = await axiosInstance.post<VerificationResponse>(
    '/admin/municipalities/refresh-stale',
    null,
    {
      params: { max_age_days: maxAgeDays },
    }
  );
  return response.data;
};

/**
 * Get municipality database statistics
 */
export const getMunicipalityStatistics = async (): Promise<MunicipalityStatistics> => {
  const response = await axiosInstance.get<MunicipalityStatistics>(
    '/admin/municipalities/statistics'
  );
  return response.data;
};

/**
 * Get verification history
 */
export const getVerificationHistory = async (
  municipality?: string,
  status?: string,
  limit: number = 50
): Promise<VerificationHistoryResponse> => {
  const response = await axiosInstance.get<VerificationHistoryResponse>(
    '/admin/verification/history',
    {
      params: { municipality, status, limit },
    }
  );
  return response.data;
};

// Search Data Management API Calls

/**
 * Get paginated list of all permit searches (admin only)
 */
export const getAdminSearches = async (
  params?: AdminSearchRequest
): Promise<AdminSearchListResponse> => {
  const response = await axiosInstance.get<AdminSearchListResponse>('/admin/searches', {
    params,
  });
  return response.data;
};

/**
 * Get detailed view of a single permit search (admin only)
 */
export const getAdminSearchDetail = async (
  searchId: string
): Promise<AdminSearchDetailResponse> => {
  const response = await axiosInstance.get<AdminSearchDetailResponse>(
    `/admin/searches/${searchId}`
  );
  return response.data;
};

/**
 * Export filtered permit searches as CSV (admin only)
 */
export const exportAdminSearchesCSV = async (params?: AdminSearchRequest): Promise<Blob> => {
  const response = await axiosInstance.get('/admin/searches/export/csv', {
    params,
    responseType: 'blob',
  });
  return response.data as Blob;
};
